import React from 'react';
import * as tf from '@tensorflow/tfjs';
import Config from './modules/config';
import T from './modules/translation';
import Message from './message';
import Version from './version';

class EngineHelper extends React.Component {
  constructor(props) {
    super(props);
    this.engine = props.engine;
    this.clearCache = this.clearCache.bind(this);
    this.setHideLessVisits = this.setHideLessVisits.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.updateDbUsed = this.updateDbUsed.bind(this);
    this.onMessage = this.onMessage.bind(this);
    this.componentCleanup = this.componentCleanup.bind(this);
    this.selectModel = React.createRef();
    this.inputMaxDbSize = React.createRef();
    this.inputNodesPerMove = React.createRef();
    this.state = {
      hideLessVisits: Config.getInstance().hideLessVisits ? true : false,
      dbUsed: "-",
      message: ""
    }
  }

  async componentDidMount() {
    this.engine.onPredict = this.props.onPredict;
    this.engine.doPonder = this.props.ponder;
    if (this.props.ponder && this.props.history.length) {
      this.engine.history = this.props.history;
    }
    await this.load();
    window.addEventListener('beforeunload', this.componentCleanup);
  }

  componentDidUpdate() {
    if (this.engine.doPonder && !this.props.ponder) {
      this.engine.doPonder = false;
      this.engine.stopPonder();
      // console.table(tf.memory());
    } else if (!this.engine.doPonder && this.props.ponder) {
      this.engine.doPonder = true;
      this.engine.nodeCounter = 0;
    }
    if (this.engine.doPonder && this.props.history.length) {
      this.engine.history = this.props.history;
    }
  }

  componentWillUnmount() {
    this.componentCleanup();
    window.removeEventListener('beforeunload', this.componentCleanup);
  }

  componentCleanup() {
    console.log("componentCleanup");
    if (this.engine) {
      this.engine.doPonder = false;
      this.engine.stopPonder();
    }
    // Chrome requires returnValue to be set
    // e.returnValue = '';
  }

  async load() {
    if (this.engine.model) {
      // console.log("Model is loaded already.");
      return;
    }
    try {
      this.engine.model = await tf.loadLayersModel(`indexeddb://${this.engine.modelName}`);
      await this.engine.model.save(`indexeddb://${this.engine.modelName}`);
      console.log("Load model from IndexedDB");
      // tf.enableDebugMode();
      this.engine.onLoad();
      // this.updateDbUsed();
    } catch (reason) {
      console.warn(reason);
      this.loadFromServer();
    }
  }

  async loadFromServer() {
    console.time("Load model from server");
    const options = {
      onProgress: (fraction) => {
        const done = (fraction * 100).toFixed();
        this.props.onMessage(`Loading model... ${done}%`);
      },
    }
    try {
      this.engine.model = await tf.loadLayersModel(this.engine.url, options);
      await this.engine.model.save(`indexeddb://${this.engine.modelName}`);
      console.log("Loaded model from " + this.engine.url);
      this.engine.onLoad();
    } catch (reason) {
      console.error(reason);
      alert(T.get("loadModelError"));
    } finally {
      console.timeEnd("Load model from server");
      this.props.onMessage("");
    }
  }

  clearCache(event) {
    if (window.confirm(T.get("confirmClearCache") + "\n" + this.engine.modelName)) {
      this.engine.predictionStore.clear();
      this.updateDbUsed();
    }
    event.preventDefault();
  }

  setHideLessVisits(event) {
    const hide = Boolean(event.target.checked);
    Config.getInstance().hideLessVisits = (hide ? 1 : 0);
    this.setState({
      hideLessVisits: hide
    })
    this.clearMessage();
  }

  async handleSubmit(event) {
    event.preventDefault();
    const config = Config.getInstance();
    let value = this.selectModel.current.value;
    if (value && this.engine.modelName !== value) {
      config.set("model", value);
      this.engine.doPonder = false;
      this.engine.stopPonder();
      this.engine.model = undefined;
      await this.load();
      this.engine.nodeCounter = 0;
      this.engine.doPonder = true;
    }
    value = this.inputNodesPerMove.current.value;
    if (!isNaN(value)) {
      config.nodesPerMove = parseInt(value);
    }
    value = this.inputMaxDbSize.current.value;
    if (!isNaN(value)) {
      config.maxDbSize = parseInt(value);
    }
    this.updateDbUsed();
    this.clearMessage();
  }

  updateDbUsed() {
    if (this.engine.predictionStore) {
      console.time("dbUsed")
      this.setState({
        dbUsed: "*" + T.get("calculating") + "*"
      });
      this.engine.predictionStore.count()
        .then(value => {
          console.timeEnd("dbUsed");
          this.setState({
            dbUsed: (parseFloat((value) / Config.getInstance().maxDbSize * 100))
              .toFixed(1)
          })
        });
    }
  }

  onMessage(text) {
    this.setState({ message: text });
  }

  clearMessage() {
    this.setState({ message: "" });
  }

  render() {
    const modelOptions = [];
    this.engine.models.forEach((model, index) => {
      modelOptions.push(<option key={index} value={model}>{model}</option>);
    })
    const defaultModel = Config.getInstance().get("model", this.engine.models[0]);
    return (
      <div className="engine-helper">
        <div className="form-check mb-3">
          <input className="form-check-input"
            type="checkbox" id="hideLessVisits"
            onChange={this.setHideLessVisits}
            checked={this.state.hideLessVisits} />
          <label className="form-check-label" htmlFor="hideLessVisits">
            {T.get("hideLessVisits")}
          </label>
        </div>
        <form onSubmit={this.handleSubmit}>
          <div className="row mb-3">
            <label htmlFor="model" className="col-auto col-form-label">{T.get("model")}</label>
            <div className="col-auto">
              <select className="form-select" id="model"
                defaultValue={defaultModel}
                ref={this.selectModel}>
                {modelOptions}
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <label htmlFor="backend" className="col-auto col-form-label">
              {T.get("backend")}
            </label>
            <div className="col-auto">
              <input className="form-control"
                type="text" id="backend"
                defaultValue={this.props.backend}
                disabled />
            </div>
          </div>
          <div className="row mb-3">
            <label htmlFor="nodesPerMove" className="col-auto col-form-label">
              {T.get("nodesPerMove")}
            </label>
            <div className="col-auto">
              <input type="number" className="form-control" id="nodesPerMove"
                min="10"
                ref={this.inputNodesPerMove}
                defaultValue={Config.getInstance().nodesPerMove} />
            </div>
          </div>
          <div className="row mb-3">
            <label htmlFor="maxDatabaseCacheSize" className="col-auto col-form-label">
              {T.get("maxDatabaseCacheSize")}:
              ({this.state.dbUsed}% {T.get("used")})
              <button type="button" className="btn btn-outline-secondary btn-sm m-2"
                aria-label="Refresh" onClick={this.updateDbUsed}>
                <span className="bi bi-arrow-clockwise" aria-hidden="true"></span>
              </button>
            </label>
            <div className="col-auto">
              <input type="number" className="form-control" id="maxDatabaseCacheSize"
                min="10000"
                ref={this.inputMaxDbSize}
                defaultValue={Config.getInstance().maxDbSize} />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col">
              <button type="submit" className="btn btn-primary btn-lg">
                {T.get("save")}
              </button>
            </div>
            <div className="col text-end">
              <button className="btn btn-outline-secondary btn-sm btn-clear-cache"
                onClick={this.clearCache}>
                {T.get("clearCache")}
              </button>
            </div>
          </div>
        </form>
        <Version />
        <Message text={this.state.message} />
      </div>
    );
  }
}

export default EngineHelper;